/* eslint-disable import/order */
import * as React from 'react';
import { useState, useMemo, useCallback, useEffect } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import {
  Tabs,
  Typography,
  Button,
  message,
  ConfigProvider
} from 'antd';

import { ExecutionHistoryTable } from './components/history/ExecutionHistoryTable';
import { ActionHistoryTable } from './components/history/ActionHistoryTable';
import { RuleFormModal } from './components/rules/RuleFormModal';
import { RulesTable } from './components/rules/RulesTable';
import { RuleBlock, RuleTableItem, PredictionModel, PredictionModelsListRequest, PredictionItem, PredictionsListRequest, CreateModelRequest, UpdateModelRequest, ActionType, ModelStatus, LearningStatus, ActiveModel, PredictionActionsHistoryRequest, PredictionActionHistoryItem } from './types';
import MainLayout from '../../shared/common/components/layouts/MainLayout';
import { predictionModelsApi } from '../../shared/common/api/endpoints/predictionModelsApi';
import { useActions } from '../../shared/common/hooks/useActions';
import { convertAntdSorterToApi } from '../../shared/common/hooks/useTableSorting';

const { Title, Text } = Typography;

const uid = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

function PurchasePrediction() {
  const { setAppReportOpen, setDrawerLink } = useActions();

  // API State for Models
  const [apiModels, setApiModels] = useState<PredictionModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  });
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // API State for Predictions
  const [apiPredictions, setApiPredictions] = useState<PredictionItem[]>([]);
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [predictionsPagination, setPredictionsPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [predictionsSortField, setPredictionsSortField] = useState<string>('prediction_at');
  const [predictionsSortDirection, setPredictionsSortDirection] = useState<'asc' | 'desc'>('desc');
  // По умолчанию — только последний прогноз (как в фильтре «Показывать» таблицы результатов)
  const [currentPredictionsFilters, setCurrentPredictionsFilters] = useState<any>({ latestOnly: true });

  // Dictionaries State
  const [dictionaries, setDictionaries] = useState<{
    actionTypes: ActionType[];
    modelStatuses: ModelStatus[];
    learningStatuses: LearningStatus[];
    activeModels: ActiveModel[];
  }>({
    actionTypes: [],
    modelStatuses: [],
    learningStatuses: [],
    activeModels: []
  });
  const [dictionariesLoading, setDictionariesLoading] = useState(false);


  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RuleTableItem | undefined>();


  // API State for Action History
  const [actionHistory, setActionHistory] = useState<PredictionActionHistoryItem[]>([]);
  const [actionHistoryLoading, setActionHistoryLoading] = useState(false);
  const [actionHistoryPagination, setActionHistoryPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [actionHistorySortField, setActionHistorySortField] = useState<string>('action_at');
  const [actionHistorySortDirection, setActionHistorySortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentActionHistoryFilters, setCurrentActionHistoryFilters] = useState<any>({});

  const [activeTab, setActiveTab] = useState<string>('rules-active');
  const [selectedModel, setSelectedModel] = useState<RuleTableItem | null>(null);

  // API Functions
  const fetchModels = useCallback(async (isArchived: boolean | null = null, page = 1, pageSize = 20, sortFieldParam = sortField, sortDirectionParam = sortDirection) => {
    setLoading(true);
    try {
      const params: PredictionModelsListRequest = {
        is_archieved: isArchived,
        page,
        pageSize,
        sortField: sortFieldParam as any,
        sortDirection: sortDirectionParam
      };

      const response = await predictionModelsApi.fetchModelsList(params);

      if (response.data.success) {
        setApiModels(response.data.data);
        setPagination(response.data.pagination);
      } else {
        message.error('Ошибка при загрузке моделей: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      message.error('Ошибка при загрузке моделей');
    } finally {
      setLoading(false);
    }
  }, [sortField, sortDirection]);

  // API Functions for Predictions
  const fetchPredictions = useCallback(async (page = 1, pageSize = 20, filters = currentPredictionsFilters, sortField = predictionsSortField, sortDirection = predictionsSortDirection) => {
    setPredictionsLoading(true);
    try {
      const params: PredictionsListRequest = {
        page,
        pageSize,
        sortField: sortField as any,
        sortDirection,
        // Добавляем фильтры
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        email: filters.email,
        prediction_min: filters.predictionMin,
        prediction_max: filters.predictionMax,
        model_ids: filters.modelIds,
        latest_only: filters.latestOnly ?? true,
      };

      const response = await predictionModelsApi.fetchPredictionsList(params);

      if (response.data.status === 'success') {
        setApiPredictions(response.data.data);
        setPredictionsPagination(response.data.pagination);
      } else {
        message.error('Ошибка при загрузке предсказаний: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching predictions:', error);
      message.error('Ошибка при загрузке предсказаний');
    } finally {
      setPredictionsLoading(false);
    }
  }, [currentPredictionsFilters]);

  // API Functions for Action History
  const fetchActionHistory = useCallback(async (page = 1, pageSize = 20, filters = currentActionHistoryFilters, sortField = actionHistorySortField, sortDirection = actionHistorySortDirection) => {
    setActionHistoryLoading(true);
    try {
      const params: PredictionActionsHistoryRequest = {
        page,
        pageSize,
        sortField: sortField as any,
        sortDirection,
        // Добавляем фильтры
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        email: filters.email,
        model_ids: filters.modelIds,
        action_type_id: filters.actionTypeId,
      };

      const response = await predictionModelsApi.fetchActionsHistory(params);

      if (response.data.status === 'success') {
        setActionHistory(response.data.data);
        setActionHistoryPagination(response.data.pagination);
      } else {
        message.error('Ошибка при загрузке истории действий: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching action history:', error);
      message.error('Ошибка при загрузке истории действий');
    } finally {
      setActionHistoryLoading(false);
    }
  }, [currentActionHistoryFilters]);

  // API Functions for Dictionaries
  const fetchDictionaries = useCallback(async () => {
    setDictionariesLoading(true);
    try {
      const response = await predictionModelsApi.getDictionaries();

      if (response.data.success) {
        setDictionaries({
          actionTypes: response.data.data.action_types,
          modelStatuses: response.data.data.model_statuses,
          learningStatuses: response.data.data.learning_statuses,
          activeModels: response.data.data.active_models
        });
      } else {
        message.error('Ошибка при загрузке справочников: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching dictionaries:', error);
      message.error('Ошибка при загрузке справочников');
    } finally {
      setDictionariesLoading(false);
    }
  }, []);

  // Load dictionaries on component mount
  useEffect(() => {
    fetchDictionaries();
  }, [fetchDictionaries]);

  // Load data when activeTab changes
  useEffect(() => {
    if (activeTab === 'rules-active') {
      fetchModels(false);
    } else if (activeTab === 'rules-archived') {
      fetchModels(true);
    } else if (activeTab === 'history') {
      fetchPredictions();
    } else if (activeTab === 'action-history') {
      fetchActionHistory();
    }
  }, [activeTab]); // Remove function dependencies to prevent unnecessary re-renders

  const handleAddRule = () => {
    setEditingRule(undefined);
    setModalOpen(true);
  };

  const handleEditRule = async (rule: RuleTableItem) => {
    try {
      // Получаем данные модели с сервера для редактирования
      const modelId = parseInt(rule.id.replace('rule-', ''));
      const response = await predictionModelsApi.getModel(modelId);

      if (response.data) {
        // Преобразуем API данные в формат RuleTableItem
        const apiModel = response.data;
        const editingRuleData: RuleTableItem = {
          id: rule.id,
          name: apiModel.model_name,
          excludeBoughtDays: apiModel.exclude_bought_days ?? 0,
          periodDays: 7, // По умолчанию
          groups: [], // TODO: Преобразовать из filters
          filters: apiModel.filters.map((filter, index) => {
            const filterKey = Object.keys(filter)[0];
            const filterValue = Object.values(filter)[0];

            const typeNames: { [key: string]: string } = {
              'list_tags': 'Тег предложения',
              'list_sentence': 'Название предложения',
              'list_tags_3': 'Тег заказа',
              'sum_order': 'Сумма заказа от и до'
            };

            if (filterKey === 'sum_order') {
              return {
                id: `filter-${index}-${uid()}`,
                type: filterKey as 'sum_order',
                name: typeNames[filterKey] || filterKey,
                rangeValue: filterValue as { start: number; end: number }
              };
            } else {
              // Для фильтров типа list_* теперь filterValue - это массив объектов с id и name
              const selectedIds = Array.isArray(filterValue)
                ? filterValue.map((item: any) => typeof item === 'object' ? item.id : item)
                : filterValue as number[];

              const selectedItems = Array.isArray(filterValue)
                ? filterValue.filter((item: any) => typeof item === 'object')
                : [];

              return {
                id: `filter-${index}-${uid()}`,
                type: filterKey as 'list_tags' | 'list_sentence' | 'list_tags_3',
                name: typeNames[filterKey] || filterKey,
                selectedIds: selectedIds,
                selectedItems: selectedItems
              };
            }
          }),
          conditions: apiModel.conditions.map((condition, conditionIndex) => ({
            id: `condition-${conditionIndex}-${uid()}`,
            // старые условия без диапазона — «от percentage до 100»
            probabilityFrom: condition.percentage_from ?? condition.percentage ?? 50,
            probabilityTo: condition.percentage_to ?? 100,
            actions: condition.actions?.map((action, actionIndex) => ({
              id: `action-${conditionIndex}-${actionIndex}-${uid()}`,
              type: action.action_type_id,
              groupName: action.action_type_id === 1 ? action.action_value : undefined,
              offerId: action.action_type_id === 2 ? action.action_value : undefined,
              fieldName: action.action_type_id === 3 ? action.action_value : undefined
            })) || []
          })),
          actions: {
            budgetAction: 'none',
            budgetAmount: 0,
            budgetUnit: 'rub',
            sendTelegram: false,
            tgMessage: '',
            tgRecipients: []
          },
          isActive: true,
          isArchived: false,
          createdAt: new Date().toLocaleDateString('ru-RU'),
          createdBy: 'Текущий пользователь',
          lastChecked: new Date().toLocaleDateString('ru-RU'),
          trainingStatus: 'success',
          accuracy: 85
        };

        setEditingRule(editingRuleData);
        setModalOpen(true);
      }
    } catch (error: any) {
      console.error('Error fetching model for editing:', error);

      // Обработка ошибок
      if (error.response?.status === 401) {
        message.error('Не авторизован');
      } else if (error.response?.status === 403) {
        message.error('Доступ запрещен');
      } else if (error.response?.status === 404) {
        message.error('Модель не найдена');
      } else if (error.response?.status === 500) {
        message.error('Внутренняя ошибка сервера');
      } else {
        message.error('Ошибка при загрузке данных модели');
      }
    }
  };

  const handleDuplicateRule = async (rule: RuleTableItem) => {
    try {
      // Получаем данные модели с сервера для дублирования
      const modelId = parseInt(rule.id.replace('rule-', ''));
      const response = await predictionModelsApi.getModel(modelId);

      if (response.data) {
        // Преобразуем API данные в формат RuleTableItem для дублирования
        const apiModel = response.data;
        const duplicatedRule: RuleTableItem = {
          id: uid(), // Новый ID для дублированной модели
          name: `${apiModel.model_name} (копия)`,
          excludeBoughtDays: apiModel.exclude_bought_days ?? 0,
          periodDays: 7, // По умолчанию
          groups: [], // TODO: Преобразовать из filters
          filters: apiModel.filters.map((filter, index) => {
            const filterKey = Object.keys(filter)[0];
            const filterValue = Object.values(filter)[0];

            const typeNames: { [key: string]: string } = {
              'list_tags': 'Тег предложения',
              'list_sentence': 'Название предложения',
              'list_tags_3': 'Тег заказа',
              'sum_order': 'Сумма заказа от и до'
            };

            if (filterKey === 'sum_order') {
              return {
                id: `filter-${index}-${uid()}`,
                type: filterKey as 'sum_order',
                name: typeNames[filterKey] || filterKey,
                rangeValue: filterValue as { start: number; end: number }
              };
            } else {
              // Для фильтров типа list_* теперь filterValue - это массив объектов с id и name
              const selectedIds = Array.isArray(filterValue)
                ? filterValue.map((item: any) => typeof item === 'object' ? item.id : item)
                : filterValue as number[];

              const selectedItems = Array.isArray(filterValue)
                ? filterValue.filter((item: any) => typeof item === 'object')
                : [];

              return {
                id: `filter-${index}-${uid()}`,
                type: filterKey as 'list_tags' | 'list_sentence' | 'list_tags_3',
                name: typeNames[filterKey] || filterKey,
                selectedIds: selectedIds,
                selectedItems: selectedItems
              };
            }
          }),
          conditions: apiModel.conditions.map((condition, conditionIndex) => ({
            id: `condition-${conditionIndex}-${uid()}`,
            // старые условия без диапазона — «от percentage до 100»
            probabilityFrom: condition.percentage_from ?? condition.percentage ?? 50,
            probabilityTo: condition.percentage_to ?? 100,
            actions: condition.actions?.map((action, actionIndex) => ({
              id: `action-${conditionIndex}-${actionIndex}-${uid()}`,
              type: action.action_type_id,
              groupName: action.action_type_id === 1 ? action.action_value : undefined,
              offerId: action.action_type_id === 2 ? action.action_value : undefined,
              fieldName: action.action_type_id === 3 ? action.action_value : undefined
            })) || []
          })),
          actions: {
            budgetAction: 'none',
            budgetAmount: 0,
            budgetUnit: 'rub',
            sendTelegram: false,
            tgMessage: '',
            tgRecipients: []
          },
          isActive: true,
          isArchived: false,
          createdAt: new Date().toLocaleDateString('ru-RU'),
          createdBy: 'Текущий пользователь',
          lastChecked: new Date().toLocaleDateString('ru-RU'),
          trainingStatus: 'success',
          accuracy: 85
        };

        setEditingRule({ ...duplicatedRule, isDuplicate: true } as any);
        setModalOpen(true);
      }
    } catch (error: any) {
      console.error('Error fetching model for duplication:', error);

      // Обработка ошибок
      if (error.response?.status === 401) {
        message.error('Не авторизован');
      } else if (error.response?.status === 403) {
        message.error('Доступ запрещен');
      } else if (error.response?.status === 404) {
        message.error('Модель не найдена');
      } else if (error.response?.status === 500) {
        message.error('Внутренняя ошибка сервера');
      } else {
        message.error('Ошибка при загрузке данных модели для дублирования');
      }
    }
  };

  const handleSaveRule = async (rule: RuleBlock): Promise<void> => {
    // Преобразуем RuleBlock в API формат
    const apiData: CreateModelRequest | UpdateModelRequest = {
      model_name: rule.name,
      exclude_bought_days: (rule.excludeBoughtDays ?? 0) as CreateModelRequest['exclude_bought_days'],
      filters: rule.filters.map(filter => {
        if (filter.type === 'sum_order') {
          // Пустую границу не отправляем: сервер принимает «только от» или «только до»
          const range: { start?: number; end?: number } = {};
          if (typeof filter.rangeValue?.start === 'number') range.start = filter.rangeValue.start;
          if (typeof filter.rangeValue?.end === 'number') range.end = filter.rangeValue.end;
          return { [filter.type]: range };
        } else {
          return { [filter.type]: filter.selectedIds };
        }
      }),
      conditions: rule.conditions.map(condition => {
        // Генерируем полный текст условия с действиями
        const actionTexts = condition.actions.map(action => {
          if (action.type === 1) {
            return action.groupName ? `добавить в группу "${action.groupName}"` : 'добавить в группу';
          }
          if (action.type === 2) {
            return action.offerId ? `создать заказ с предложением "${action.offerId}"` : 'создать заказ с предложением';
          }
          if (action.type === 3) {
            return action.fieldName ? `изменить значение доп. поля "${action.fieldName}"` : 'изменить значение доп. поля';
          }
          return '';
        }).filter(Boolean);

        const fullConditionText = `Если вероятность покупки от ${condition.probabilityFrom} до ${condition.probabilityTo}%, то ${actionTexts.join(', ')}`;

        return {
          percentage_from: condition.probabilityFrom,
          percentage_to: condition.probabilityTo,
          condition_text: fullConditionText,
          actions: condition.actions.map(action => ({
            action_type_id: action.type,
            action_value: action.offerId || action.groupName || action.fieldName || ''
          }))
        };
      })
    };

    if (editingRule && !editingRule.isDuplicate) {
      // Обновление существующей модели
      const modelId = parseInt(editingRule.id.replace('rule-', ''));
      const response = await predictionModelsApi.updateModel(modelId, apiData);

      if (response.data.model_id) {
        message.success('Модель успешно обновлена');
        setModalOpen(false);
        // Обновляем данные в таблице
        const isArchived = activeTab === 'rules-archived';
        fetchModels(isArchived);
      } else {
        throw new Error('Не удалось обновить модель');
      }
    } else {
      // Создание новой модели
      const response = await predictionModelsApi.createModel(apiData);

      if (response.data.model_id) {
        message.success('Модель успешно создана');
        setModalOpen(false);
        // Обновляем данные в таблице
        const isArchived = activeTab === 'rules-archived';
        fetchModels(isArchived);
      } else {
        throw new Error('Не удалось создать модель');
      }
    }
  };

  const handleDeleteRule = async (id: string) => {
    try {
      const response = await predictionModelsApi.updateModelArchive(parseInt(id), { is_archieved: true });
      if (response.data.success) {
        message.success('Модель перенесена в архив');
        // Refresh data
        const isArchived = activeTab === 'rules-archived';
        fetchModels(isArchived);
      } else {
        message.error('Ошибка при архивировании: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error archiving model:', error);
      message.error('Ошибка при архивировании модели');
    }
  };

  const handleUnarchiveRule = async (id: string) => {
    try {
      const response = await predictionModelsApi.updateModelArchive(parseInt(id), { is_archieved: false });
      if (response.data.success) {
        message.success('Модель восстановлена из архива');
        // Refresh data
        const isArchived = activeTab === 'rules-archived';
        fetchModels(isArchived);
      } else {
        message.error('Ошибка при восстановлении: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error unarchiving model:', error);
      message.error('Ошибка при восстановлении модели');
    }
  };

  const handleResetActions = async (id: string) => {
    try {
      const response = await predictionModelsApi.resetModelActions(parseInt(id));
      if (response.data.success) {
        message.success('Действия будут выполнены заново при ближайшем прогнозе');
      } else {
        message.error('Ошибка при сбросе истории действий: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error resetting model actions:', error);
      message.error('Ошибка при сбросе истории действий');
    }
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      const statusId = isActive ? 1 : 2; // 1: Включена, 2: Отключена
      const response = await predictionModelsApi.updateModelStatus(parseInt(id), { model_status_id: statusId });
      if (response.data.success) {
        message.success(isActive ? 'Модель включена' : 'Модель отключена');
        // Refresh data
        const isArchived = activeTab === 'rules-archived';
        fetchModels(isArchived);
      } else {
        message.error('Ошибка при изменении статуса: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error updating model status:', error);
      message.error('Ошибка при изменении статуса модели');
    }
  };

  const handleModelClick = (rule: RuleTableItem) => {
    setSelectedModel(rule);
    setActiveTab('history');
  };

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    if (tab === 'rules-active' || tab === 'rules-archived') {
      setSelectedModel(null);
    }
  }, []);

  const handleTableChange = useCallback((
    paginationInfo: { current?: number; pageSize?: number }, 
    filters: unknown, 
    sorter: { field?: string; order?: 'ascend' | 'descend' | null }
  ) => {
    // Используем утилиту для конвертации сортировки
    const apiSort = convertAntdSorterToApi(sorter);
    const sortFieldFromSorter = Object.keys(apiSort)[0];
    
    const newSortField = sortFieldFromSorter || sortField;
    const newSortDirection = sortFieldFromSorter 
      ? (apiSort[sortFieldFromSorter] === 'ASC' ? 'asc' : 'desc') as 'asc' | 'desc'
      : sortDirection;

    if (sortFieldFromSorter) {
      // Update state but don't wait for it
      setSortField(newSortField);
      setSortDirection(newSortDirection);
    }

    // Call fetchModels with new sorting parameters immediately
    const isArchived = activeTab === 'rules-archived';
    fetchModels(isArchived, paginationInfo.current || pagination.currentPage, paginationInfo.pageSize || pagination.pageSize, newSortField, newSortDirection);
  }, [activeTab, pagination, sortField, sortDirection, fetchModels]);

  const handlePredictionsTableChange = useCallback((paginationInfo: any, filters: any, sorter: any) => {
    let newSortField = predictionsSortField;
    let newSortDirection = predictionsSortDirection;

    if (sorter.field) {
      newSortField = sorter.field;
      newSortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
      // Update state but don't wait for it
      setPredictionsSortField(newSortField);
      setPredictionsSortDirection(newSortDirection);
    }

    // Call fetchPredictions with new sorting parameters immediately
    fetchPredictions(
      paginationInfo.current || predictionsPagination.page,
      paginationInfo.pageSize || predictionsPagination.pageSize,
      currentPredictionsFilters,
      newSortField,
      newSortDirection
    );
  }, [predictionsPagination, currentPredictionsFilters, predictionsSortField, predictionsSortDirection]);

  const handlePredictionsFiltersChange = useCallback((filters: any) => {
    // Сохраняем текущие фильтры
    setCurrentPredictionsFilters(filters);

    // Вызываем fetchPredictions с новыми фильтрами и текущей сортировкой
    fetchPredictions(1, predictionsPagination.pageSize, filters, predictionsSortField, predictionsSortDirection);
  }, [predictionsPagination.pageSize, predictionsSortField, predictionsSortDirection]);

  const handleActionHistoryTableChange = useCallback((paginationInfo: any, filters: any, sorter: any) => {
    let newSortField = actionHistorySortField;
    let newSortDirection = actionHistorySortDirection;

    if (sorter.field) {
      newSortField = sorter.field;
      newSortDirection = sorter.order === 'ascend' ? 'asc' : 'desc';
      // Update state but don't wait for it
      setActionHistorySortField(newSortField);
      setActionHistorySortDirection(newSortDirection);
    }

    // Call fetchActionHistory with new sorting parameters immediately
    fetchActionHistory(
      paginationInfo.current || actionHistoryPagination.page,
      paginationInfo.pageSize || actionHistoryPagination.pageSize,
      currentActionHistoryFilters,
      newSortField,
      newSortDirection
    );
  }, [actionHistoryPagination, currentActionHistoryFilters, actionHistorySortField, actionHistorySortDirection]);

  const handleActionHistoryFiltersChange = useCallback((filters: any) => {
    // Сохраняем текущие фильтры
    setCurrentActionHistoryFilters(filters);

    // Вызываем fetchActionHistory с новыми фильтрами и текущей сортировкой
    fetchActionHistory(1, actionHistoryPagination.pageSize, filters, actionHistorySortField, actionHistorySortDirection);
  }, [actionHistoryPagination.pageSize, actionHistorySortField, actionHistorySortDirection]);

  // Handler for opening help drawer
  const handleOpenHelpDrawer = useCallback(() => {
    setDrawerLink('https://help.scananalytics.ru/predskazanie-pokupki/');
    setAppReportOpen(true);
  }, [setDrawerLink, setAppReportOpen]);

  const tabItems = useMemo(() => [
    { key: 'rules-active', label: 'Модели предсказания' },
    { key: 'history', label: 'Результаты предсказания' },
    { key: 'action-history', label: 'История действий' },
    { key: 'rules-archived', label: 'Архивные модели' },
  ], []);

  return (
    <MainLayout pageTitle="Предсказание вероятности покупки">
      <div className="page-wrapper">
        <div className="page-content">
          <ConfigProvider>
            <div>
              <style>{`
          .scope-toggle { 
            display: inline-flex; 
            border-radius: 8px; 
          }
          .scope-btn { 
            background: #fff; 
            color: #000; 
            border: 1px solid #d9d9d9; 
            padding: 6px 14px; 
            line-height: 1.2; 
            cursor: pointer; 
            user-select: none; 
            transition: all 0.2s ease;
            font-size: 14px;
          }
          .scope-btn + .scope-btn { 
            margin-left: -1px; 
          }
          .scope-btn.is-first { 
            border-radius: 8px 0 0 8px; 
          }
          .scope-btn.is-last { 
            border-radius: 0 8px 8px 0; 
          }
          .scope-btn.is-active { 
            background: #1677ff; 
            color: #fff; 
            border-color: #1677ff; 
            position: relative; 
            z-index: 1; 
          }
          .scope-btn:not(.is-active):hover { 
            color: #1677ff; 
            background: #f0f8ff; 
            border-color: #1677ff;
          }
          .scope-btn:focus-visible { 
            outline: 2px solid #1677ff; 
            outline-offset: 1px; 
          }
          .adaptive-cascader-popup .ant-cascader-menu {
            max-height: none !important;
            height: auto !important;
          }
          .adaptive-cascader-popup .ant-cascader-menus {
            max-height: none !important;
            height: auto !important;
          }
          
          /* Fix ResizeObserver issues */
          .ant-table {
            contain: layout;
          }
          .ant-table-tbody > tr > td {
            contain: layout;
          }
          .ant-table-thead > tr > th {
            contain: layout;
          }
          .ant-tabs {
            contain: layout;
          }
          .ant-tabs-nav {
            contain: layout;
          }
          .ant-tabs-content-holder {
            contain: layout;
          }
        `}</style>

              <div style={{
                minHeight: '100vh',
                background: '#ffffff'
              }}>
                <div style={{ padding: 24 }}>

                  <div style={{ marginBottom: 24 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16
                    }}>
                      <Title level={1} style={{ margin: 0, color: '#000', fontSize: '23px', fontWeight: '600' }}>
                        Предсказание вероятности покупки
                      </Title>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <div style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: '#ff4d4f',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ?
                        </div>
                        <Text
                          style={{
                            color: '#ff4d4f',
                            fontSize: '14px',
                            cursor: 'pointer'
                          }}
                          onClick={handleOpenHelpDrawer}
                        >
                          Как создать предсказание покупки
                        </Text>
                      </div>
                    </div>
                  </div>

                  {modalOpen && (
                    <RuleFormModal
                      open={modalOpen}
                      onCancel={() => setModalOpen(false)}
                      onSave={handleSaveRule}
                      editingRule={editingRule}
                      dictionaries={dictionaries}
                    />
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16
                  }}>
                    <Tabs
                      activeKey={activeTab}
                      onChange={handleTabChange}
                      style={{ marginBottom: 0, minWidth: '600px' }}
                      items={tabItems}
                    />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAddRule}
                      style={{
                        backgroundColor: '#ff4d4f',
                        borderColor: '#ff4d4f',
                        borderRadius: '8px',
                        height: '40px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Создать модель предсказания
                    </Button>
                  </div>

                  <div>
                    {(activeTab === 'rules-active' || activeTab === 'rules-archived') && (
                      <RulesTable
                        apiModels={apiModels}
                        loading={loading}
                        pagination={pagination}
                        onEdit={handleEditRule}
                        onDelete={handleDeleteRule}
                        onUnarchive={handleUnarchiveRule}
                        onToggleStatus={handleToggleStatus}
                        onAdd={handleAddRule}
                        onDuplicate={handleDuplicateRule}
                        onResetActions={handleResetActions}
                        onTableChange={handleTableChange}
                        activeTab={activeTab === 'rules-active' ? 'active' : 'archived'}
                        onModelClick={handleModelClick}
                      />
                    )}
                    {activeTab === 'history' && (
                      <ExecutionHistoryTable
                        selectedModelName={selectedModel?.name || null}
                        apiPredictions={apiPredictions}
                        loading={predictionsLoading}
                        pagination={predictionsPagination}
                        onTableChange={handlePredictionsTableChange}
                        onFiltersChange={handlePredictionsFiltersChange}
                        activeModels={dictionaries.activeModels}
                      />
                    )}
                    {activeTab === 'action-history' && (
                      <ActionHistoryTable
                        history={actionHistory}
                        loading={actionHistoryLoading}
                        pagination={actionHistoryPagination}
                        onTableChange={handleActionHistoryTableChange}
                        onFiltersChange={handleActionHistoryFiltersChange}
                        actionTypes={dictionaries.actionTypes}
                        activeModels={dictionaries.activeModels}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </ConfigProvider>
        </div>
      </div>
    </MainLayout>
  );
}

export default PurchasePrediction;

